import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, Phone, FacebookLogo, InstagramLogo } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { COMPANY, IMAGES } from "../data/site";

const NAV = [
  { to: "/", label: "Accueil", slug: "accueil" },
  { to: "/prestations", label: "Prestations", slug: "prestations" },
  { to: "/realisations", label: "Réalisations", slug: "realisations" },
  { to: "/a-propos", label: "À propos", slug: "a-propos" },
  { to: "/contact", label: "Contact", slug: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      data-testid="navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-[#F0F0F0] bg-white/90 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 md:px-12">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5">
          <img src={IMAGES.logo} alt="Logo Lisse Energies" className="h-9 w-9 object-contain" />
          <span className="text-lg font-extrabold tracking-tight text-[#14183A]">
            Lisse <span className="text-[#1A49F7]">Energies</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.slug}`}
              className={({ isActive }) =>
                `nav-link text-sm font-medium transition-colors ${
                  isActive ? "active text-[#14183A]" : "text-[#666]"
                } hover:text-[#14183A]`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <a
            href={COMPANY.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-facebook"
            aria-label="Facebook Lisse Energies"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1A49F7] text-white shadow-sm transition-all duration-300 hover:scale-110 hover:shadow-[0_8px_20px_-6px_rgba(26,73,247,0.7)]"
          >
            <FacebookLogo weight="fill" className="h-5 w-5" />
          </a>
          <a
            href={COMPANY.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-instagram"
            aria-label="Instagram Lisse Energies"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white shadow-sm transition-all duration-300 hover:scale-110 hover:shadow-[0_8px_20px_-6px_rgba(255,49,35,0.6)]"
            style={{ background: "linear-gradient(45deg, #FC8529, #FF3123, #1A49F7)" }}
          >
            <InstagramLogo weight="fill" className="h-5 w-5" />
          </a>
          <a
            href={COMPANY.phoneLink}
            data-testid="navbar-phone"
            className="ml-1 hidden items-center gap-2 text-sm font-semibold text-[#14183A] xl:inline-flex"
          >
            <Phone weight="fill" className="h-4 w-4 text-[#FF3123]" />
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="navbar-cta"
            className="hidden rounded-full bg-[#14183A] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:shadow-[0_8px_24px_-6px_rgba(26,73,247,0.5)] lg:inline-block"
          >
            Nous contacter
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="navbar-burger"
            aria-label="Menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EEE] lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <List className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#F0F0F0] bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.slug}`}
                  className={({ isActive }) =>
                    `border-b border-[#F5F5F5] py-4 text-2xl font-semibold tracking-tight ${
                      isActive ? "text-[#1A49F7]" : "text-[#14183A]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="mt-6 flex flex-col gap-3">
                <Link
                  to="/contact"
                  className="rounded-full bg-[#14183A] px-6 py-3.5 text-center font-semibold text-white"
                >
                  Nous contacter
                </Link>
                <a
                  href={COMPANY.phoneLink}
                  className="flex items-center justify-center gap-2 rounded-full border border-[#E5E5E5] px-6 py-3.5 font-semibold"
                >
                  <Phone weight="fill" className="h-4 w-4" /> {COMPANY.phone}
                </a>
                <div className="mt-2 flex items-center justify-center gap-4">
                  <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1A49F7] text-white">
                    <FacebookLogo weight="fill" className="h-6 w-6" />
                  </a>
                  <a href={COMPANY.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="inline-flex h-12 w-12 items-center justify-center rounded-full text-white" style={{ background: "linear-gradient(45deg, #FC8529, #FF3123, #1A49F7)" }}>
                    <InstagramLogo weight="fill" className="h-6 w-6" />
                  </a>
                </div>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
